import EventSchedule20 from '@carbon/icons-react/es/event--schedule/20';
import Event20 from '@carbon/icons-react/es/event/20';
import Help16 from '@carbon/icons-react/es/help/16';
import { SecureRoute } from '@okta/okta-react';
import { Col, Row, Tooltip, ConfigProvider } from 'antd';
import moment from 'moment';
import React from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { Link, Switch, useHistory, useLocation } from 'react-router-dom';
import { FieldSectionType } from 'api/field-sections/types';
import { Page } from 'components/molecules/page';
import { RoutePaths } from 'constant';
import { useTranslation } from 'hooks/use-translation';
import {
  useKeyEvents,
  useIntervals,
} from 'utils/project-fields/use-project-fields';
import { EditIntervalPage } from '../intervals/edit-interval-page';
import { NewIntervalPage } from '../intervals/new-interval-page';
import { EditEventPage } from '../key-events/edit-event-page';
import { NewKeyeventPage } from '../key-events/new-event-page';
import { MoreOption } from '../list-item-component';
import {
  StyledText,
  StyledPage,
  HeaderWrapper,
  Header,
  HelpText,
  PageContentWrapper,
  AddFieldWrapper,
  BarWrapper,
  TableHeader,
  StyledAddButton,
  StyledTable,
  TitleWrapper,
  Title,
  TitleDescription,
  TableTitleWrapper,
} from './styles';
import {
  DurationBetweenKeyEventsRenderer,
  EventEmptyState,
  GetNameElement,
  IntervalEmptyState,
  TextWithTooltip,
  UserNameWithTooltip,
} from './utils';

export const KeyeventsAndIntervalsSettings = (): JSX.Element => {
  const title = useTranslation('settings.keyevents_and_intervals.title');

  const keyEvents = useKeyEvents();
  const intervals = useIntervals();

  const history = useHistory();
  const onClose = () => history.push(RoutePaths.keyeventsAndIntervals);

  /* a minimum of two key events are required to create an interval,
  thus we block creation of interval if condition is not met */
  const isCreateIntervalEnabled = keyEvents.length >= 2;

  const { pathname } = useLocation();

  const eventTableColumns = [
    {
      title: (
        <FormattedMessage id="settings.keyevents_and_intervals.table_column.name" />
      ),
      dataIndex: 'name',
      width: 200,
      fixed: 'left' as const,
    },
    {
      title: (
        <FormattedMessage id="settings.keyevents_and_intervals.table_column.description" />
      ),
      dataIndex: 'description',
      width: 300,
    },
    {
      title: (
        <FormattedMessage id="settings.keyevents_and_intervals.table_column.lastUpdatedOn" />
      ),
      dataIndex: 'lastUpdatedOn',
      width: 150,
    },
    {
      title: (
        <FormattedMessage id="settings.keyevents_and_intervals.table_column.lastUpdatedBy" />
      ),
      dataIndex: 'lastUpdatedBy',
      width: 200,
    },
    {
      title: (
        <FormattedMessage id="settings.keyevents_and_intervals.table_column.createdOn" />
      ),
      dataIndex: 'createdOn',
      width: 150,
    },
    {
      title: (
        <FormattedMessage id="settings.keyevents_and_intervals.table_column.createdBy" />
      ),
      dataIndex: 'createdBy',
      width: 200,
    },
    { title: '', dataIndex: 'moreActions', fixed: 'right' as const, width: 50 },
  ];
  const intervalTableColumns = [
    {
      title: (
        <FormattedMessage id="settings.keyevents_and_intervals.table_column.name" />
      ),
      dataIndex: 'name',
      width: 200,
      fixed: 'left' as const,
    },
    {
      title: (
        <FormattedMessage id="settings.keyevents_and_intervals.table_column.description" />
      ),
      dataIndex: 'description',
      width: 300,
    },
    {
      title: (
        <FormattedMessage id="settings.keyevents_and_intervals.table_column.durationBetweenKeyEvents" />
      ),
      dataIndex: 'durationBetweenKeyEvents',
      width: 300,
    },
    {
      title: (
        <FormattedMessage id="settings.keyevents_and_intervals.table_column.lastUpdatedOn" />
      ),
      dataIndex: 'lastUpdatedOn',
      width: 150,
    },
    {
      title: (
        <FormattedMessage id="settings.keyevents_and_intervals.table_column.lastUpdatedBy" />
      ),
      dataIndex: 'lastUpdatedBy',
      name: 200,
    },
    {
      title: (
        <FormattedMessage id="settings.keyevents_and_intervals.table_column.createdOn" />
      ),
      dataIndex: 'createdOn',
      width: 150,
    },
    {
      title: (
        <FormattedMessage id="settings.keyevents_and_intervals.table_column.createdBy" />
      ),
      dataIndex: 'createdBy',
      name: 200,
    },
    { title: '', dataIndex: 'moreActions', fixed: 'right' as const, width: 50 },
  ];
  const eventTableData = keyEvents.map((event) => {
    const { updatedBy, createdBy } = event;

    return {
      name: (
        <TextWithTooltip
          dataCy={`key-event.${event.fieldLabel}`}
          text={event.fieldLabel ?? ''}
        />
      ),
      description:
        event.description !== '' ? (
          <TextWithTooltip
            dataCy={`key-event-desc.${event.fieldLabel}`}
            text={event.description ?? ''}
          />
        ) : (
          <>-</>
        ),
      lastUpdatedOn: event?.updatedAt ? (
        moment(event?.updatedAt).format('DD MMM YYYY')
      ) : (
        <>-</>
      ),
      lastUpdatedBy: <UserNameWithTooltip id={updatedBy} />,
      createdOn: event.createdAt ? (
        moment(event.createdAt).format('DD MMM YYYY')
      ) : (
        <>-</>
      ),
      createdBy: <UserNameWithTooltip id={createdBy} />,
      moreActions: (
        <MoreOption
          dataCy={`key-event.more-actions.${event.fieldLabel}`}
          field={event}
        />
      ),
    };
  });
  const intervalTableData = intervals.map((interval) => {
    const { updatedBy, createdBy } = interval;

    return {
      name: (
        <TextWithTooltip
          text={interval.fieldLabel ?? ''}
          dataCy={`interval.${interval?.fieldLabel}`}
        />
      ),
      description:
        interval.description !== '' ? (
          <StyledText ellipsis={{ tooltip: true }}>
            {interval.description}
          </StyledText>
        ) : (
          <>-</>
        ),
      durationBetweenKeyEvents: (
        <DurationBetweenKeyEventsRenderer
          interval={interval}
          keyEvents={keyEvents}
        />
      ),
      lastUpdatedOn: interval?.updatedAt ? (
        moment(interval?.updatedAt).format('DD MMM YYYY')
      ) : (
        <>-</>
      ),
      lastUpdatedBy: <GetNameElement id={updatedBy} />,
      createdOn: interval.createdAt ? (
        moment(interval.createdAt).format('DD MMM YYYY')
      ) : (
        <>-</>
      ),
      createdBy: <GetNameElement id={createdBy} />,
      moreActions: (
        <MoreOption
          field={interval}
          dataCy={`interval.more-actions.${interval.fieldLabel}`}
        />
      ),
    };
  });

  return (
    <>
      <StyledPage>
        <Helmet>{title}</Helmet>
        <HeaderWrapper>
          <Page.Header
            $white
            title={
              <Header>
                <Page.Header.Title>{title}</Page.Header.Title>
                <HelpText />
                <a
                  href="https://help.rocketlane.com/support/solutions/articles/67000717327-key-events-and-intervals-in-rocketlane"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FormattedMessage id="learn_more" />
                </a>
              </Header>
            }
          />
        </HeaderWrapper>
        <PageContentWrapper>
          <div>
            <AddFieldWrapper>
              <BarWrapper>
                <TableHeader>
                  <Row>
                    <Col flex={1}>
                      <TableTitleWrapper>
                        <Event20 color="#697077" />
                        <strong>
                          <FormattedMessage id="settings.events.title" />
                        </strong>
                        <Tooltip
                          title={
                            <FormattedMessage id="settings.events.tooltip" />
                          }
                          placement="rightTop"
                        >
                          <Help16 />
                        </Tooltip>
                      </TableTitleWrapper>
                    </Col>
                  </Row>
                </TableHeader>
                <Link to={RoutePaths.newKeyevent}>
                  <StyledAddButton data-cy="key-events.add-new-button">
                    <FormattedMessage id="settings.events.new" />
                  </StyledAddButton>
                </Link>
              </BarWrapper>
            </AddFieldWrapper>
            <ConfigProvider renderEmpty={() => <EventEmptyState />}>
              <StyledTable
                columns={eventTableColumns}
                dataSource={eventTableData}
                pagination={false}
                scroll={{ x: 1300, y: 300 }}
                bordered
              />
            </ConfigProvider>
          </div>

          <div>
            <AddFieldWrapper>
              <BarWrapper>
                <TableHeader>
                  <Row>
                    <Col flex={1}>
                      <TableTitleWrapper>
                        <EventSchedule20 color="#697077" />
                        <strong>
                          <FormattedMessage id="settings.intervals.title" />
                        </strong>
                        <Tooltip
                          title={
                            <FormattedMessage id="settings.intervals.tooltip" />
                          }
                          placement="rightTop"
                        >
                          <Help16 />
                        </Tooltip>
                      </TableTitleWrapper>
                    </Col>
                  </Row>
                </TableHeader>
                <Link
                  to={
                    !isCreateIntervalEnabled ? pathname : RoutePaths.newInterval
                  }
                >
                  <Tooltip
                    title={
                      !isCreateIntervalEnabled ? (
                        <FormattedMessage id="settings.intervals.disabled.message" />
                      ) : (
                        ''
                      )
                    }
                    placement="top"
                  >
                    <div>
                      <StyledAddButton data-cy="intervals.add-new-button">
                        <FormattedMessage id="settings.intervals.new" />
                      </StyledAddButton>
                    </div>
                  </Tooltip>
                </Link>
              </BarWrapper>
            </AddFieldWrapper>
            <ConfigProvider
              renderEmpty={() => (
                <IntervalEmptyState
                  isCreateIntervalEnabled={isCreateIntervalEnabled}
                />
              )}
            >
              <StyledTable
                columns={intervalTableColumns}
                dataSource={intervalTableData}
                pagination={false}
                scroll={{ x: 1400, y: 300 }}
                bordered
              />
            </ConfigProvider>
          </div>

          <Switch>
            <SecureRoute path={RoutePaths.newKeyevent}>
              <NewKeyeventPage
                onClose={onClose}
                title={
                  <TitleWrapper>
                    <Title>
                      <FormattedMessage id="settings.events.create.title" />
                    </Title>
                    <TitleDescription>
                      <FormattedMessage id="settings.events.create.description" />
                    </TitleDescription>
                  </TitleWrapper>
                }
              />
            </SecureRoute>
            <SecureRoute path={RoutePaths.editKeyevent}>
              <EditEventPage
                onClose={onClose}
                title={<FormattedMessage id="settings.events.edit" />}
              />
            </SecureRoute>
            <SecureRoute path={RoutePaths.newInterval}>
              <NewIntervalPage
                onClose={onClose}
                title={
                  <TitleWrapper>
                    <Title>
                      <FormattedMessage id="settings.intervals.create.title" />
                    </Title>
                    <TitleDescription>
                      <FormattedMessage id="settings.intervals.create.description" />
                    </TitleDescription>
                  </TitleWrapper>
                }
                objectType={FieldSectionType.Project}
              />
            </SecureRoute>
            <SecureRoute path={RoutePaths.editInterval}>
              <EditIntervalPage
                onClose={onClose}
                title={<FormattedMessage id="settings.intervals.edit" />}
              />
            </SecureRoute>
          </Switch>
        </PageContentWrapper>
      </StyledPage>
    </>
  );
};
